import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { of, switchMap, bufferTime, filter, map, mergeMap } from 'rxjs';
import { ENTITY_LIBRARY_ACTIONS } from '../army-builder-entity-store';

/**
 * Creates a batching effect that buffers LOAD_ENTITY_DATA_COMPLETE actions
 * and dispatches them as a single LOAD_ENTITY_DATA_BATCH action.
 * This dramatically reduces change detection cycles during initial entity load.
 */
export function createEntityLoadBatchingEffect(actions$: Actions) {
    return createEffect(() =>
        actions$.pipe(
            ofType(ENTITY_LIBRARY_ACTIONS.LOAD_ENTITY_DATA_COMPLETE),
            // Buffer completed entity loads that arrive within 100ms
            bufferTime(100),
            filter((actions) => actions.length > 0),
            map((actions: any[]) => {
                // Single batch action that updates all entity types at once
                // This triggers only ONE state update and ONE change detection cycle
                const updates = actions.map((action) => ({
                    gameId: action.gameId,
                    dataType: action.dataType,
                    data: action.data
                }));

                console.log(`[ENTITY-LOAD-BATCH] Batching ${updates.length} entity types into single state update`);

                return ENTITY_LIBRARY_ACTIONS.LOAD_ENTITY_DATA_BATCH({ updates });
            })
        )
    );
}

@Injectable()
export class DefaultEntityLibraryEffects {
    // Transform LOAD_ENTITY_DATA_AVAILABLE to LOAD_ENTITY_DATA_COMPLETE (passthrough)
    interceptAction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ENTITY_LIBRARY_ACTIONS.LOAD_ENTITY_DATA_AVAILABLE),
            map((action: any) => {
                return ENTITY_LIBRARY_ACTIONS.LOAD_ENTITY_DATA_COMPLETE({
                    gameId: action.gameId,
                    dataType: action.dataType,
                    data: action.data
                });
            })
        )
    );

    // Batch LOAD_ENTITY_DATA_COMPLETE actions to reduce change detection
    batchLoads$ = createEntityLoadBatchingEffect(this.actions$);

    constructor(private actions$: Actions) {}
}
